import React, { useMemo } from "react";
import { AgGrid, AgGridReact, Card, Flex, Grid, Text } from "@jsq/ds/core";
import { PaymentServicePaymentBatchStatus } from "#graphql/supergraphEnums";
import { GetAccountingPayableForSlideoutQuery } from "#graphql/supergraphTypes";
import { ActionType } from "../../../common/components/ActionButton";
import { getColumnDefinitions } from "../../DistributionSlideout/PaymentBatchesTable/columnDefinitions";
import {
  cardTitle,
  paymentBatchesCard,
  tableContainer,
} from "../../DistributionSlideout/PaymentBatchesTable/styles_vanilla.css";
import { PaymentBatchRowItem } from "../../DistributionSlideout/PaymentBatchesTable/types";
import { usePaymentsContext } from "../../PaymentsListWorkflow/Context/PaymentsContext";
import { getCombinedStatusConfig } from "../../shared/combinedStatusUtil";

type AccountingPayable = NonNullable<GetAccountingPayableForSlideoutQuery["accountingPayable"]>;

type PaymentBatchNode = AccountingPayable["paymentBatches"][number];

export type PaymentBatchesTableProps = {
  paymentBatches: PaymentBatchNode[];
  loading?: boolean;
  onReviewClick?: (paymentBatch: PaymentBatchRowItem) => void;
};

const REVIEW_ACTION: ActionType = "review";

const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  enableCellTextSelection: true,
  suppressCellFocus: true,
  suppressContextMenu: true,
  domLayout: "autoHeight",
};

const defaultColumnOptions: AgGrid.ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
};

const mapBatchesToRowData = (
  batches: PaymentBatchNode[],
  isGPX?: boolean,
): PaymentBatchRowItem[] => {
  return batches.map((batch) => {
    const isReadyForReview =
      batch.status === PaymentServicePaymentBatchStatus.ReadyForTreasuryReview;
    const action: ActionType = isReadyForReview ? REVIEW_ACTION : null;

    const payments = batch.payments.map((payment) => ({
      objectId: payment.objectId,
      status: payment.status,
      method: payment.method,
      amount: payment.amount,
      scheduledProcessDate: payment.scheduledProcessDate,
    }));

    const combinedStatusConfig = getCombinedStatusConfig(
      batch.status,
      payments,
      isGPX,
      undefined,
    );

    return {
      id: batch.id,
      createdDate: batch.createdAt,
      objectsCount: batch.objectsCount,
      netAmount: batch.netAmount,
      status: batch.status,
      payments,
      combinedStatusConfig,
      creatorId: batch.creatorId,
      action,
    };
  });
};

export const PaymentBatchesTable: React.FC<PaymentBatchesTableProps> = ({
  paymentBatches,
  loading,
  onReviewClick,
}) => {
  const { isGPX } = usePaymentsContext();

  const rowData = useMemo<PaymentBatchRowItem[]>(() => {
    return mapBatchesToRowData(paymentBatches, isGPX);
  }, [paymentBatches, isGPX]);

  const showActionColumn = useMemo(() => {
    return rowData.some((batch) => batch.action === REVIEW_ACTION);
  }, [rowData]);

  const columnDefs = useMemo(() => {
    return getColumnDefinitions(showActionColumn, { onReviewClick });
  }, [showActionColumn, onReviewClick]);

  const noRowsTemplate = `<span>No payment batches have been created</span>`;

  return (
    <Card className={paymentBatchesCard}>
      <Flex flexDirection="column">
        <Text className={cardTitle} variant="secondary-md">
          PAYMENT BATCHES
        </Text>
        <Flex className={tableContainer}>
          <Grid<PaymentBatchRowItem>
            key={showActionColumn ? "with-action" : "without-action"}
            columnDefs={columnDefs}
            defaultColDef={defaultColumnOptions}
            getRowId={(params) => params.data.id}
            gridId="payment_batches_for_object"
            gridOptions={gridOptions}
            overlayNoRowsTemplate={noRowsTemplate}
            reactiveCustomComponents
            rowData={loading ? null : rowData}
          />
        </Flex>
      </Flex>
    </Card>
  );
};
